import { toValue, type MaybeRefOrGetter } from 'vue';
import { onDispose, unrefElement, type NxMaybeElement } from './element';

export interface NxClickOutsideOptions {
  /** Also call the handler on Escape. Default `false`. */
  escape?: boolean;
  /**
   * Presses here don't count as outside — the trigger button of a popover,
   * say, which toggles on its own. Elements or CSS selectors.
   */
  ignore?: readonly (NxMaybeElement | string)[];
  /** Listen only while this is true (the popover is open). Default `true`. */
  enabled?: MaybeRefOrGetter<boolean>;
}

/**
 * Calls `handler` when the pointer goes down outside every one of `targets` —
 * the usual way to close a popover, menu or inline editor. Listens in the
 * capture phase, so a click that something else stops still counts, and
 * follows `composedPath()` so presses inside a shadow root are seen as inside.
 *
 * While none of the targets is rendered there is nothing to be outside of and
 * the handler stays quiet. Returns a `stop` function; the listeners also go
 * when the calling component (or effect scope) is disposed.
 */
export function useNxClickOutside(
  targets: NxMaybeElement | readonly NxMaybeElement[],
  handler: (event: PointerEvent | KeyboardEvent) => void,
  options: NxClickOutsideOptions = {}
): () => void {
  if (typeof document === 'undefined') return () => {};
  const list = (Array.isArray(targets) ? targets : [targets]) as readonly NxMaybeElement[];
  const enabled = () => toValue(options.enabled) ?? true;

  const inside = (event: Event, el: Element) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(el)) return true;
    const target = event.target;
    return target instanceof Node && el.contains(target);
  };

  const ignored = (event: Event) =>
    (options.ignore ?? []).some((entry) => {
      if (typeof entry === 'string') {
        const target = event.target;
        return target instanceof Element && !!target.closest(entry);
      }
      const el = unrefElement(entry);
      return !!el && inside(event, el);
    });

  const onPointerDown = (event: PointerEvent) => {
    if (!enabled()) return;
    const elements = list.map(unrefElement).filter((el): el is Element => !!el);
    if (!elements.length) return;
    if (elements.some((el) => inside(event, el)) || ignored(event)) return;
    handler(event);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || event.defaultPrevented || !enabled()) return;
    if (!list.some((target) => unrefElement(target))) return;
    handler(event);
  };

  document.addEventListener('pointerdown', onPointerDown, true);
  if (options.escape) document.addEventListener('keydown', onKeyDown);

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('keydown', onKeyDown);
  };
  onDispose(stop);
  return stop;
}
